import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArrowLeft, Database, Layers, BarChart3, FileText, GitBranch,
  ZoomIn, ZoomOut, Maximize2, Loader2, Info, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { ConditionalFormulaBreakdown } from '@/components/ConditionalFormulaBreakdown';

const NODE_COLORS = {
  data_source: { bg: 'bg-emerald-500/15', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: Database },
  entity: { bg: 'bg-blue-500/15', border: 'border-blue-500/30', text: 'text-blue-400', icon: Layers },
  field: { bg: 'bg-indigo-500/15', border: 'border-indigo-500/30', text: 'text-indigo-400', icon: BarChart3 },
  calculated: { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-400', icon: GitBranch },
  report: { bg: 'bg-rose-500/15', border: 'border-rose-500/30', text: 'text-rose-400', icon: FileText },
};

const EDGE_COLORS = {
  sources: '#10b981',
  contains: '#3b82f6',
  derives: '#f59e0b',
  used_in: '#ef4444',
  joins: '#8b5cf6',
};

const LAYER_LABELS = ['Data Sources', 'Entities', 'Fields', 'Calculations', 'Reports & Visuals'];
const LAYER_Y_OFFSETS = [60, 200, 380, 520, 660];
const NODE_W = 180;
const NODE_H = 48;

function layoutNodes(nodes) {
  const layers = {};
  nodes.forEach(n => {
    const layerIdx = Math.round(n.layer);
    if (!layers[layerIdx]) layers[layerIdx] = [];
    layers[layerIdx].push(n);
  });

  const positioned = [];
  Object.entries(layers).sort(([a], [b]) => Number(a) - Number(b)).forEach(([layerIdx, layerNodes]) => {
    const li = Number(layerIdx);
    const y = LAYER_Y_OFFSETS[li] || (li * 160 + 60);
    const totalWidth = layerNodes.length * (NODE_W + 24);
    const startX = Math.max(40, 600 - totalWidth / 2);
    layerNodes.forEach((node, i) => {
      positioned.push({
        ...node,
        x: startX + i * (NODE_W + 24),
        y,
        w: NODE_W,
        h: NODE_H,
      });
    });
  });
  return positioned;
}

function LineageSVG({ nodes, edges, zoom, panX, panY, selectedNode, onSelectNode }) {
  const nodeMap = {};
  nodes.forEach(n => { nodeMap[n.id] = n; });

  return (
    <svg
      width="100%"
      height="100%"
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
    >
      <g transform={`translate(${panX}, ${panY}) scale(${zoom})`}>
        {/* Draw edges */}
        {edges.map((edge, i) => {
          const from = nodeMap[edge.from];
          const to = nodeMap[edge.to];
          if (!from || !to) return null;
          const x1 = from.x + from.w / 2;
          const y1 = from.y + from.h;
          const x2 = to.x + to.w / 2;
          const y2 = to.y;
          const midY = (y1 + y2) / 2;
          const isHighlighted = selectedNode && (edge.from === selectedNode || edge.to === selectedNode);
          return (
            <path
              key={i}
              d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
              fill="none"
              stroke={isHighlighted ? EDGE_COLORS[edge.type] || '#6b7280' : 'rgba(107, 114, 128, 0.2)'}
              strokeWidth={isHighlighted ? 2.5 : 1.2}
              strokeDasharray={edge.type === 'joins' ? '6,4' : 'none'}
              style={{ pointerEvents: 'none', transition: 'stroke 0.2s, stroke-width 0.2s' }}
              markerEnd={isHighlighted ? 'url(#arrowhead)' : undefined}
            />
          );
        })}
        {/* Arrowhead marker */}
        <defs>
          <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
          </marker>
        </defs>
      </g>
    </svg>
  );
}

export default function LineagePage() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');
  const [lineageData, setLineageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(0.85);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    api.getSemanticModels().then(ms => {
      setModels(ms || []);
      if (ms?.length > 0) setSelectedModelId(ms[0].model_id);
    }).catch(() => toast.error('Failed to load models'));
  }, []);

  const loadLineage = useCallback(async () => {
    if (!selectedModelId) return;
    setLoading(true);
    setSelectedNode(null);
    try {
      const data = await api.getDataLineage(selectedModelId);
      setLineageData(data);
      setPan({ x: 0, y: 0 });
    } catch (err) {
      toast.error('Failed to load lineage: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedModelId]);

  useEffect(() => { loadLineage(); }, [loadLineage]);

  const positionedNodes = lineageData ? layoutNodes(lineageData.nodes) : [];
  const selectedNodeData = positionedNodes.find(n => n.id === selectedNode);
  const connectedEdges = lineageData?.edges?.filter(e => e.from === selectedNode || e.to === selectedNode) || [];
  const connectedNodeIds = new Set(connectedEdges.flatMap(e => [e.from, e.to]));

  const handleMouseDown = (e) => {
    if (e.target.closest('[data-lineage-node]')) return;
    setDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setDragging(false);
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.05 : 0.05;
    setZoom(z => Math.max(0.3, Math.min(2, z + delta)));
  };

  return (
    <div className="h-full flex flex-col" data-testid="lineage-page">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/50 flex items-center gap-4" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-lg w-8 h-8" data-testid="lineage-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-lg font-heading font-bold text-foreground flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-primary" />
            Data Lineage
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">Trace field origins from data source to visual</p>
        </div>
        <Select value={selectedModelId} onValueChange={setSelectedModelId}>
          <SelectTrigger className="w-64 rounded-lg bg-muted/30 border-border/30" data-testid="lineage-model-select">
            <SelectValue placeholder="Select model..." />
          </SelectTrigger>
          <SelectContent>
            {models.map(m => (
              <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.min(2, z + 0.15))} className="w-7 h-7 rounded" data-testid="lineage-zoom-in">
            <ZoomIn className="w-3.5 h-3.5" />
          </Button>
          <span className="text-[10px] text-muted-foreground font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
          <Button variant="ghost" size="icon" onClick={() => setZoom(z => Math.max(0.3, z - 0.15))} className="w-7 h-7 rounded" data-testid="lineage-zoom-out">
            <ZoomOut className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => { setZoom(0.85); setPan({ x: 0, y: 0 }); }} className="w-7 h-7 rounded" data-testid="lineage-reset-zoom">
            <Maximize2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {lineageData?.summary && (
        <div className="px-6 py-2 border-b border-border/30 flex items-center gap-4" style={{ background: 'var(--glass-bg)' }}>
          {[
            { label: 'Data Sources', value: lineageData.summary.data_sources, color: 'text-emerald-400' },
            { label: 'Entities', value: lineageData.summary.entities, color: 'text-blue-400' },
            { label: 'Fields', value: lineageData.summary.fields, color: 'text-indigo-400' },
            { label: 'Calculated', value: lineageData.summary.calculated_measures, color: 'text-amber-400' },
            { label: 'Reports', value: lineageData.summary.reports, color: 'text-rose-400' },
          ].map(s => (
            <div key={s.label} className="flex items-center gap-1.5" data-testid={`lineage-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <span className={`text-lg font-bold font-heading ${s.color}`}>{s.value}</span>
              <span className="text-[10px] text-muted-foreground">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Canvas + Detail Panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Canvas */}
        <div
          ref={containerRef}
          className="flex-1 relative overflow-hidden"
          style={{ background: 'radial-gradient(circle at 50% 50%, rgba(59,130,246,0.03), transparent 70%)', cursor: dragging ? 'grabbing' : 'grab' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          data-testid="lineage-canvas"
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center z-20 bg-background/50 backdrop-blur-sm">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {/* Layer labels */}
          {!loading && lineageData && LAYER_Y_OFFSETS.map((y, i) => (
            <div
              key={i}
              className="absolute text-[9px] text-muted-foreground/70 uppercase tracking-wider font-semibold"
              style={{ transform: `translate(${pan.x + 12}px, ${pan.y + y * zoom - 14}px)`, pointerEvents: 'none' }}
            >
              {LAYER_LABELS[i]}
            </div>
          ))}

          {/* SVG Edges */}
          {lineageData && (
            <LineageSVG
              nodes={positionedNodes}
              edges={lineageData.edges}
              zoom={zoom}
              panX={pan.x}
              panY={pan.y}
              selectedNode={selectedNode}
              onSelectNode={setSelectedNode}
            />
          )}

          {/* Nodes */}
          {positionedNodes.map(node => {
            const style = NODE_COLORS[node.type] || NODE_COLORS.field;
            const Icon = style.icon;
            const isSelected = selectedNode === node.id;
            const isConnected = connectedNodeIds.has(node.id);
            const dimmed = selectedNode && !isSelected && !isConnected;

            return (
              <div
                key={node.id}
                data-lineage-node
                data-testid={`lineage-node-${node.id}`}
                className={`absolute rounded-xl border ${style.border} ${style.bg} backdrop-blur-sm cursor-pointer transition-all duration-200 ${isSelected ? 'ring-2 ring-primary shadow-lg shadow-primary/20 scale-105' : dimmed ? 'opacity-30' : 'hover:scale-[1.03] hover:shadow-md'}`}
                style={{
                  left: pan.x + node.x * zoom,
                  top: pan.y + node.y * zoom,
                  width: node.w * zoom,
                  height: node.h * zoom,
                  zIndex: isSelected ? 20 : 10,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedNode(prev => prev === node.id ? null : node.id);
                }}
              >
                <div className="flex items-center gap-2 h-full px-3" style={{ transform: `scale(${Math.min(1, zoom)})`, transformOrigin: 'left center' }}>
                  <Icon className={`w-4 h-4 ${style.text} flex-shrink-0`} />
                  <div className="min-w-0 flex-1">
                    <p className={`text-[10px] font-medium ${style.text} truncate`}>{node.name || node.id}</p>
                    {node.field_name && <p className="text-[8px] text-muted-foreground font-mono truncate">{node.field_name}</p>}
                    {node.fields != null && <p className="text-[8px] text-muted-foreground">{node.fields} fields</p>}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Empty state */}
          {!loading && !lineageData && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <GitBranch className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Select a semantic model to view its data lineage</p>
              </div>
            </div>
          )}

          {!loading && lineageData && positionedNodes.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Info className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No lineage data found for this model</p>
                <p className="text-xs text-muted-foreground/80 mt-1">Ensure the model has entities and fields configured</p>
              </div>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedNodeData && (
          <div className="w-72 border-l border-border/50 flex flex-col" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }} data-testid="lineage-detail-panel">
            <div className="p-4 border-b border-border/30">
              <div className="flex items-center gap-2 mb-2">
                {(() => { const s = NODE_COLORS[selectedNodeData.type] || NODE_COLORS.field; const I = s.icon; return <div className={`p-2 rounded-lg ${s.bg} ${s.border} border`}><I className={`w-4 h-4 ${s.text}`} /></div>; })()}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-heading font-semibold text-foreground truncate">{selectedNodeData.name}</p>
                  <Badge variant="outline" className="text-[8px] mt-0.5">{selectedNodeData.type.replace('_', ' ')}</Badge>
                </div>
              </div>
              {selectedNodeData.field_name && (
                <p className="text-[10px] text-muted-foreground font-mono mt-1">Field: {selectedNodeData.field_name}</p>
              )}
              {selectedNodeData.formula && (
                <div className="mt-2 p-2 rounded-lg bg-muted/20 border border-border/20">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1">Formula</p>
                  <p className="text-[10px] font-mono text-amber-400">{selectedNodeData.formula}</p>
                </div>
              )}
              {selectedNodeData.formula && (
                <ConditionalFormulaBreakdown formula={selectedNodeData.formula} testId="lineage-conditional-breakdown" />
              )}
              {selectedNodeData.fields != null && (
                <p className="text-[10px] text-muted-foreground mt-1">{selectedNodeData.fields} fields in entity</p>
              )}
            </div>
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-3">
                {/* Upstream */}
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5">Upstream (Sources)</p>
                  {connectedEdges.filter(e => e.to === selectedNode).map((edge, i) => {
                    const fromNode = positionedNodes.find(n => n.id === edge.from);
                    if (!fromNode) return null;
                    const s = NODE_COLORS[fromNode.type] || NODE_COLORS.field;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedNode(edge.from)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted/20 transition-colors text-left"
                        data-testid={`lineage-upstream-${edge.from}`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${s.text.replace('text-', 'bg-')}`} />
                        <span className="text-[10px] text-foreground truncate flex-1">{fromNode.name}</span>
                        <Badge variant="outline" className="text-[7px] flex-shrink-0">{edge.type}</Badge>
                        <ChevronRight className="w-3 h-3 text-muted-foreground/70" />
                      </button>
                    );
                  })}
                  {connectedEdges.filter(e => e.to === selectedNode).length === 0 && (
                    <p className="text-[9px] text-muted-foreground/70 italic">No upstream dependencies</p>
                  )}
                </div>
                {/* Downstream */}
                <div>
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold mb-1.5">Downstream (Used By)</p>
                  {connectedEdges.filter(e => e.from === selectedNode).map((edge, i) => {
                    const toNode = positionedNodes.find(n => n.id === edge.to);
                    if (!toNode) return null;
                    const s = NODE_COLORS[toNode.type] || NODE_COLORS.field;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedNode(edge.to)}
                        className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-muted/20 transition-colors text-left"
                        data-testid={`lineage-downstream-${edge.to}`}
                      >
                        <div className={`w-1.5 h-1.5 rounded-full ${s.text.replace('text-', 'bg-')}`} />
                        <span className="text-[10px] text-foreground truncate flex-1">{toNode.name}</span>
                        <Badge variant="outline" className="text-[7px] flex-shrink-0">{edge.type}</Badge>
                        <ChevronRight className="w-3 h-3 text-muted-foreground/70" />
                      </button>
                    );
                  })}
                  {connectedEdges.filter(e => e.from === selectedNode).length === 0 && (
                    <p className="text-[9px] text-muted-foreground/70 italic">No downstream consumers</p>
                  )}
                </div>
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="px-6 py-2 border-t border-border/30 flex items-center gap-6" style={{ background: 'var(--glass-bg)' }} data-testid="lineage-legend">
        {Object.entries(NODE_COLORS).map(([type, style]) => {
          const Icon = style.icon;
          return (
            <div key={type} className="flex items-center gap-1.5">
              <div className={`p-1 rounded ${style.bg} ${style.border} border`}>
                <Icon className={`w-3 h-3 ${style.text}`} />
              </div>
              <span className="text-[9px] text-muted-foreground capitalize">{type.replace('_', ' ')}</span>
            </div>
          );
        })}
        <div className="ml-auto flex items-center gap-4">
          {Object.entries(EDGE_COLORS).map(([type, color]) => (
            <div key={type} className="flex items-center gap-1.5">
              <div className="w-4 h-0.5 rounded" style={{ background: color, borderStyle: type === 'joins' ? 'dashed' : 'solid' }} />
              <span className="text-[9px] text-muted-foreground capitalize">{type}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
